import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { FileText, Plus, Edit3, Trash2, Calendar, Search } from "lucide-react";

const NotesPage = ({ auth }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [formData, setFormData] = useState({ title: "", content: "", chapter: "" });

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/notes`, {
        withCredentials: true,
        headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
      });
      setNotes(response.data);
    } catch (error) {
      console.error("Error fetching notes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error("Please fill in title and content");
      return;
    }

    try {
      if (editingNote) {
        await axios.put(`${API}/notes/${editingNote.note_id}`, formData, {
          withCredentials: true,
          headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
        });
        toast.success("Note updated!");
      } else {
        await axios.post(`${API}/notes`, formData, {
          withCredentials: true,
          headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
        });
        toast.success("Note created!");
      }
      
      fetchNotes();
      setIsDialogOpen(false);
      setEditingNote(null);
      setFormData({ title: "", content: "", chapter: "" });
    } catch (error) {
      toast.error("Failed to save note");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm("Are you sure you want to delete this note?")) return;

    try {
      await axios.delete(`${API}/notes/${noteId}`, {
        withCredentials: true,
        headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
      });
      toast.success("Note deleted");
      fetchNotes();
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  const openEditDialog = (note) => {
    setEditingNote(note);
    setFormData({
      title: note.title,
      content: note.content,
      chapter: note.chapter || ""
    });
    setIsDialogOpen(true);
  };

  const filteredNotes = notes.filter(note => 
    note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout auth={auth}>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-maple border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout auth={auth}>
      <div className="space-y-8" data-testid="notes-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">My Notes</h1>
            <p className="text-slate-600">Keep track of important information</p>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingNote(null);
              setFormData({ title: "", content: "", chapter: "" });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-maple hover:bg-maple-600 text-white" data-testid="add-note-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Note
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingNote ? "Edit Note" : "Create New Note"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Title</label>
                  <Input
                    placeholder="Note title..."
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    data-testid="note-title-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Chapter (optional)</label>
                  <Input
                    placeholder="e.g., Canadian History"
                    value={formData.chapter}
                    onChange={(e) => setFormData({ ...formData, chapter: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Content</label>
                  <Textarea
                    placeholder="Write your notes here..."
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={6}
                    data-testid="note-content-input"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveNote}
                    className="bg-maple hover:bg-maple-600 text-white"
                    data-testid="save-note-btn"
                  >
                    {editingNote ? "Update" : "Save"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search notes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-notes"
          />
        </div>

        {/* Notes Grid */}
        {filteredNotes.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredNotes.map((note) => (
              <Card key={note.note_id} className="border border-slate-200 hover:border-maple/50 hover:shadow-md transition-all" data-testid={`note-card-${note.note_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-heading font-semibold text-slate-900 line-clamp-1">
                      {note.title}
                    </h3>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(note)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit3 className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteNote(note.note_id)}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {note.chapter && (
                    <span className="inline-block text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded mb-3">
                      {note.chapter}
                    </span>
                  )}

                  <p className="text-slate-600 text-sm line-clamp-4 mb-4">
                    {note.content}
                  </p>

                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Calendar className="w-3 h-3" />
                    {new Date(note.updated_at).toLocaleDateString()}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                {searchTerm ? "No notes found" : "No notes yet"}
              </h3>
              <p className="text-slate-600 mb-6">
                {searchTerm 
                  ? "Try a different search term" 
                  : "Create your first note to keep track of important citizenship information"}
              </p>
              {!searchTerm && (
                <Button 
                  onClick={() => setIsDialogOpen(true)}
                  className="bg-maple hover:bg-maple-600 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Note
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default NotesPage;
